import React, { useRef, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/api/queryKeys';
import {
  ProductImageApiError,
  productImages,
} from '@/api/ProductImages/ProductImages';
import type {
  GenerateProductImageRequest,
  ImageDescriptor,
  ImageVariantResult,
} from '@/api/ProductImages/types';
import {
  executableVariant,
  matchingVariantGeneration,
} from '@/api/ProductImages/generationVariants';
import { useTranslate } from '@/translations';
import { PrivateImage } from '@/modules/ProductImages/PrivateImage';
import {
  variantImageMessage,
  variantLabel,
  variantPresentation,
} from '@/modules/ProductImages/variantImageMessages';

export const RetryVariantImageDialog = ({
  productId,
  productCode,
  image,
  result,
  onClose,
}: {
  productId: number;
  productCode: string;
  image: ImageDescriptor;
  result: ImageVariantResult;
  onClose: () => void;
}) => {
  const { t } = useTranslate();
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ProductImageApiError | null>(null);
  const sending = useRef(false);
  const request = useRef<GenerateProductImageRequest | null>(null);
  const unknown =
    matchingVariantGeneration(image, result)?.status === 'Unknown';
  const presentation = variantPresentation(result.variantKey);
  const style = variantLabel(t, result.variantKey);
  const unavailable =
    !result.canRetry ||
    !executableVariant(result.variantKey) ||
    image.sourceKind !== 'Generated';
  const submit = async () => {
    if (
      sending.current ||
      unavailable ||
      !reason.trim() ||
      (unknown && !acknowledged)
    )
      return;
    sending.current = true;
    request.current ??= {
      idempotencyKey: crypto.randomUUID(),
      expectedCurrentRevisionId: image.assetId,
      reason: reason.trim(),
      acknowledgeUnknownOutcome: acknowledged,
    };
    setSubmitting(true);
    setError(null);
    try {
      await productImages.retryVariant(
        productId,
        result.variantKey,
        request.current,
      );
      await client.invalidateQueries({ queryKey: [QUERY_KEYS.productImagery] });
      onClose();
    } catch (failure) {
      const apiError =
        failure instanceof ProductImageApiError
          ? failure
          : new ProductImageApiError(500);
      if (apiError.status === 400) request.current = null;
      setError(apiError);
      if ([403, 409].includes(apiError.status))
        await client.invalidateQueries({
          queryKey: [QUERY_KEYS.productImagery],
        });
    } finally {
      sending.current = false;
      setSubmitting(false);
    }
  };
  return (
    <Modal
      open
      centered
      title={t(presentation.confirmTitle, { product: productCode, style })}
      onCancel={submitting ? undefined : onClose}
      onOk={() => void submit()}
      confirmLoading={submitting}
      okText={t(presentation.confirm)}
      cancelText={t('imagery.cancel')}
      okButtonProps={{
        disabled:
          unavailable ||
          !reason.trim() ||
          (unknown && !acknowledged) ||
          (!!error && [403, 404, 409, 501].includes(error.status)),
      }}
      destroyOnHidden
    >
      <p>{t(presentation.confirmDescription, { style })}</p>
      <p>
        {t('imagery.sourceOutline')}:{' '}
        {t('imagery.revision', { revision: image.revision })}
      </p>
      <PrivateImage
        src={image.thumbnailUrl ?? image.contentUrl}
        assetId={image.assetId}
        thumbnail
        label={t('imagery.outlineImage')}
      />
      <p className="mt-3">{t('imagery.confirmQuota')}</p>
      {unknown ? (
        <Alert
          type="warning"
          showIcon
          message={t('imagery.unknownWarning')}
          className="mb-3"
        />
      ) : null}
      <Form layout="vertical">
        <Form.Item
          label={t('imagery.reason')}
          htmlFor="imagery-variant-reason"
          required
        >
          <Input.TextArea
            id="imagery-variant-reason"
            autoFocus
            value={reason}
            maxLength={500}
            rows={3}
            disabled={!!request.current}
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Item>
        {unknown ? (
          <Checkbox
            checked={acknowledged}
            disabled={!!request.current}
            onChange={(event) => setAcknowledged(event.target.checked)}
          >
            {t('imagery.unknownAcknowledge')}
          </Checkbox>
        ) : null}
      </Form>
      {error ? (
        <Alert
          className="mt-3"
          type="error"
          showIcon
          message={variantImageMessage(
            t,
            error.reason,
            error.status === 409
              ? 'imagery.requestConflict'
              : error.status === 403
                ? 'imagery.requestDenied'
                : [404, 501].includes(error.status)
                  ? 'imagery.apiUnavailable'
                  : 'imagery.requestFailed',
          )}
        />
      ) : null}
    </Modal>
  );
};
